import { useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { getToken } from "../utils/auth";
import { dev_baseUrl } from "../utils/var";

const useSignalR = (onNotificationReceived) => {
  const [connection, setConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const connectionRef = useRef(null);
  const onNotificationReceivedRef = useRef(onNotificationReceived);

  // Keep the ref updated
  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived;
  }, [onNotificationReceived]);

  useEffect(() => {
    const startConnection = async () => {
      try {
        const token = getToken();
        if (!token) return;

        // Don't create a new connection if one already exists
        if (
          connectionRef.current &&
          connectionRef.current.state !==
            signalR.HubConnectionState.Disconnected
        ) {
          return;
        }
        const newConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${dev_baseUrl}/NotificationHub`, {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
            accessTokenFactory: () => token,
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        newConnection.on("ReceiveNotification", (message) => {
          onNotificationReceivedRef.current?.(message);
        });

        newConnection.onclose(() => setConnectionStatus("disconnected"));
        newConnection.onreconnecting(() => setConnectionStatus("reconnecting"));
        newConnection.onreconnected(() => setConnectionStatus("connected"));

        await newConnection.start();
        setConnection(newConnection);
        setConnectionStatus("connected");
        connectionRef.current = newConnection;
      } catch (err) {
        console.error("SignalR Connection Error:", err);
        setConnectionStatus("error");
        // Retry after a delay
        setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.off("ReceiveNotification");
        connectionRef.current.stop();
      }
    };
  }, []); // Empty dependency array since we're using refs

  return { connection, connectionStatus };
};

export default useSignalR;
